import React, { useMemo } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, Stack } from '@mui/material';
import { CalendarTodayRounded, DateRangeRounded, CalendarMonthRounded } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

const PRESETS = [
  { key: 'next7', label: 'Next 7 days', from: () => dayjs(), to: () => dayjs().add(7, 'day'), icon: <CalendarTodayRounded fontSize="small" /> },
  { key: 'next30', label: 'Next 30 days', from: () => dayjs(), to: () => dayjs().add(30, 'day'), icon: <DateRangeRounded fontSize="small" /> },
  { key: 'next60', label: 'Next 60 days', from: () => dayjs(), to: () => dayjs().add(60, 'day'), icon: <CalendarMonthRounded fontSize="small" /> },
];

const DatePresetsToggle = ({ onApply }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = useMemo(() => ({
    start: searchParams.get('StartDate') || '',
    end: searchParams.get('EndDate') || '',
  }), [searchParams]);

  const selected = useMemo(() => {
    const fmt = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : '');
    const curStart = fmt(values.start);
    const curEnd = fmt(values.end);
    const match = PRESETS.find(p => p.from().format('YYYY-MM-DD') === curStart && p.to().format('YYYY-MM-DD') === curEnd);
    return match ? match.key : null;
  }, [values.start, values.end]);

  const setParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === '' || v == null) next.delete(k); else next.set(k, v);
    });
    next.set('PageNumber', '0');
    setSearchParams(next);
  };

  const handleChange = (_e, key) => {
    if (!key) {
      setParams({ StartDate: '', EndDate: '' });
      if (onApply) onApply();
      return;
    }
    const p = PRESETS.find(pr => pr.key === key);
    if (!p) return;
    setParams({
      StartDate: p.from().format('YYYY-MM-DD'),
      EndDate: p.to().format('YYYY-MM-DD'),
    });
    if (onApply) onApply();
  };

  return (
    <Box sx={{ mt: 1.25 }}>
      <ToggleButtonGroup
        value={selected}
        exclusive
        onChange={handleChange}
        size="small"
        sx={{ flexWrap: 'wrap', gap: 1 }}
      >
        {PRESETS.map((p) => (
          <ToggleButton
            key={p.key}
            value={p.key}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 1,
              bgcolor: selected === p.key ? '#E8F0FE' : 'background.paper',
              color: selected === p.key ? 'primary.main' : 'text.primary',
            }}
          >
            <Tooltip title={`${p.from().format('YYYY-MM-DD')} → ${p.to().format('YYYY-MM-DD')}`} arrow>
              <span>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {p.icon}
                  <span>{p.label}</span>
                </Stack>
              </span>
            </Tooltip>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

export default DatePresetsToggle;
